// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * The best name for a chat (ADR-0048 §5), shared by the chat view and the
 * app-level call host (which names the caller on a ringing banner shown over
 * whatever screen the user is on).
 *
 * A 1x1 chat is the other person, through the one display resolver (local
 * rename, profile name, verified username, fingerprint), read from the live
 * MLS roster so it works however the chat was created. A group keeps its label;
 * a linked contact's rename still names an accept-created chat.
 *
 * Which of the two a conversation IS follows the kind the conversation carries
 * (ADR-0062, `conversation-kind.ts`), not the member count: a two-person GROUP is titled
 * as a group (ADR-0051 shared name first, then the member-derived title), the
 * same way the list files it, so a rename reaches the header, the confirmations
 * and the ringing banner. Without `isGroup` the two-member roster used to win
 * here and the header read the other person's name over the group's.
 */

import type { Group as WasmGroup } from "../../wasm/lakko_ffi_wasm";
import { personKeysOf } from "./person-key";
import { t } from "../l10n";

export interface PeerNameInputs {
  /** This account's signature public key, base64, or `null` when unknown. */
  readonly ownKeyB64: string | null;
  /** Every roster member's signature public key, base64. */
  readonly memberKeysB64: readonly string[];
  /** The ADR-0048 display resolver. */
  readonly resolveName: (signatureKeyB64: string) => string;
  /** A linked contact's user-chosen name for this chat, if any. */
  readonly linkedContactName: string | undefined;
  /** The chat's label — for a group, already resolved with the ADR-0051
   *  precedence (shared name, else the label, else the members' names). */
  readonly label: string;
  /** `true` when the conversation is filed as a GROUP (the kind the conversation carries, ADR-0062),
   *  whatever its size: the peer-name branches are skipped and `label` is
   *  the title. Defaults to the size-based rule. */
  readonly isGroup?: boolean;
}

/** Pure half of the rule. */
export function peerName(inputs: PeerNameInputs): string {
  if (inputs.isGroup === true) return inputs.label.length > 0 ? inputs.label : t("chat.group.fallback_name");
  const peers = inputs.memberKeysB64.filter((k) => k !== inputs.ownKeyB64);
  if (inputs.memberKeysB64.length === 2 && peers.length === 1 && peers[0] !== undefined) {
    return inputs.resolveName(peers[0]);
  }
  if (inputs.linkedContactName !== undefined && inputs.linkedContactName.length > 0) {
    return inputs.linkedContactName;
  }
  return inputs.label.length > 0 ? inputs.label : t("chat.peer.fallback_name");
}

/** The roster's PEOPLE — distinct account keys (ADR-0050), so a 1x1 between
 *  two people with three devices each is still a 1x1. A torn group reads as
 *  empty (label fallback). */
export function memberKeysOf(group: WasmGroup): string[] {
  return personKeysOf(group);
}
